<template>
  <v-container :class="containerClass" fluid>
    <v-card flat>
      <v-card-title>
        <div class="d-flex align-center">
          <v-btn
            icon
            color="primary"
            left
            :href="$route('classroom-schedules.index')"
          >
            <v-icon>mdi-arrow-left</v-icon>
          </v-btn>
          <h2>{{ schedule.classroom.name }}</h2>
        </div>
      </v-card-title>
      <v-card-text>
        <v-container fluid>
          <v-row>
            <v-col cols="12">
              <v-card class="mb-4">
                <v-card-title>Class Info</v-card-title>
                <v-divider></v-divider>
                <v-card-text>
                  <v-container fluid>
                    <v-row>
                      <v-col cols="12" md="2">
                        <h3>Class Name</h3>
                        <div>
                          {{ schedule.classroom.name }}
                        </div>
                      </v-col>
                      <v-col cols="12" md="2">
                        <h3>Class Type</h3>
                        <div>
                          {{
                            classroomTypeProps(
                              schedule.classroom.class_type,
                              'text'
                            )
                          }}
                        </div>
                      </v-col>
                      <v-col cols="12" md="2">
                        <h3>Teacher</h3>
                        <div>{{ schedule.classroom.teacher.name }}</div>
                      </v-col>
                      <v-col cols="12" md="2">
                        <h3>Lesson Plan</h3>
                        <div>
                          {{
                            schedule.classroom.subscription_package.lesson_plan
                              .name
                          }}
                        </div>
                      </v-col>
                      <v-col cols="12" md="2">
                        <h3>Status</h3>
                        <div>
                          <v-chip
                            :color="
                              classroomStatusProps(
                                schedule.classroom.status,
                                'color'
                              )
                            "
                            small
                          >
                            {{
                              classroomStatusProps(
                                schedule.classroom.status,
                                'text'
                              )
                            }}
                          </v-chip>
                        </div>
                      </v-col>
                      <v-col cols="12" md="2">
                        <h3>Online Class URL</h3>
                        <div>
                          <a
                            v-if="schedule.classroom.online_class_url"
                            :href="schedule.classroom.online_class_url"
                          >
                            Open online class here
                          </a>
                          <span v-else>-</span>
                        </div>
                      </v-col>
                    </v-row>
                    <v-row
                      v-if="schedule.lessons && schedule.lessons.length > 0"
                    >
                      <v-col cols="12">
                        <h3>Lessons</h3>
                        <v-list dense>
                          <v-list-item
                            v-for="(lesson, i) in schedule.lessons"
                            :key="i"
                          >
                            <v-list-item-content>
                              <v-list-item-title>
                                <h4>
                                  {{ bilingualText(lesson, 'name', 'name_cn') }}
                                </h4>
                              </v-list-item-title>
                              <v-list-item-subtitle>
                                {{ getCategoryName(lesson.lesson_category_id) }}
                              </v-list-item-subtitle>
                            </v-list-item-content>
                          </v-list-item>
                        </v-list>
                        <v-btn
                          v-if="isLessonsDownloadable(schedule)"
                          outlined
                          small
                          color="success"
                          :href="
                            $route(
                              'classroom-schedules.download-lessons',
                              schedule.id
                            )
                          "
                        >
                          <v-icon left>mdi-cloud-download</v-icon>Download Files
                        </v-btn>
                      </v-col>
                    </v-row>
                  </v-container>
                </v-card-text>
              </v-card>
            </v-col>
            <v-col cols="12">
              <v-card class="mb-4">
                <v-card-title> Class Schedule </v-card-title>
                <v-divider></v-divider>
                <v-card-text>
                  <v-container fluid>
                    <v-row>
                      <v-col cols="12" md="4">
                        <h3>Date / Time</h3>
                        <span class="mr-4">{{
                          formatDate(form.class_date, 'DD MMM YYYY')
                        }}</span>
                        <span>
                          {{ formatTime(form.start_time) }} -
                          {{ formatTime(form.end_time) }}
                        </span>
                      </v-col>
                      <v-col cols="12" md="2">
                        <h3>Session</h3>
                        {{ form.session }}
                      </v-col>
                      <v-col cols="12" md="2">
                        <h3>Month</h3>
                        {{ form.month }}
                      </v-col>
                      <v-col cols="12" md="2">
                        <h3>Term</h3>
                        {{ form.term }}
                      </v-col>
                      <v-col
                        v-if="isTeacherAccount"
                        cols="12"
                        md="2"
                        class="text-right"
                      >
                        <reschedule-dialog
                          v-model="show"
                          :item="form"
                          @submit="onReschedule"
                        >
                          <template #activator="{ on, attrs }">
                            <v-btn
                              depressed
                              color="success"
                              v-bind="attrs"
                              v-on="on"
                            >
                              <v-icon left>mdi-calendar</v-icon> Re-Schedule
                            </v-btn>
                          </template>
                        </reschedule-dialog>
                        <v-tooltip bottom>
                          <template #activator="{ on, attrs }">
                            <v-icon v-bind="attrs" right small v-on="on">
                              mdi-help-circle
                            </v-icon>
                          </template>
                          <span>
                            You are allowed to re-schedule for today's or
                            upcoming class only.
                          </span>
                        </v-tooltip>
                      </v-col>
                    </v-row>
                  </v-container>
                </v-card-text>
              </v-card>
            </v-col>
            <v-col
              v-if="schedule.lessons && schedule.lessons.length > 0"
              cols="12"
            >
              <v-card class="mb-4">
                <v-card-title>Study Materials</v-card-title>
                <v-divider></v-divider>
                <v-card-text>
                  <v-list flat>
                    <v-list-item v-if="!$vuetify.breakpoint.mobile">
                      <v-container fluid class="pa-0">
                        <v-row>
                          <v-col><h4>Lesson</h4></v-col>
                          <v-col><h4>Category</h4></v-col>
                          <v-col><h4>Material File(s)</h4></v-col>
                        </v-row>
                      </v-container>
                    </v-list-item>
                    <template v-for="(item, i) in schedule.lessons">
                      <v-list-item :key="i">
                        <v-list-item-content>
                          <v-container fluid class="pa-0">
                            <v-row>
                              <v-col cols="12" md="4">
                                <h4 v-if="$vuetify.breakpoint.mobile">
                                  Lesson
                                </h4>
                                {{ bilingualText(item, 'name', 'name_cn') }}
                              </v-col>
                              <v-col cols="12" md="4">
                                <h4 v-if="$vuetify.breakpoint.mobile">
                                  Category
                                </h4>
                                {{ getCategoryName(item.lesson_category_id) }}
                              </v-col>
                              <v-col cols="12" md="4">
                                <h4 v-if="$vuetify.breakpoint.mobile">
                                  Material File(s)
                                </h4>
                                <div v-if="item.attachment_file_url == null">
                                  -
                                </div>
                                <ul v-else>
                                  <li
                                    v-for="(
                                      attachment_url, j
                                    ) in item.attachment_file_url.split(',')"
                                    :key="j"
                                  >
                                    <a :href="attachment_url" target="_blank">
                                      {{ filenameFromUrl(attachment_url) }}
                                    </a>
                                  </li>
                                </ul>
                              </v-col>
                            </v-row>
                          </v-container>
                        </v-list-item-content>
                      </v-list-item>
                      <v-divider
                        v-if="i < schedule.lessons.length - 1"
                        :key="`div-${i}`"
                      ></v-divider>
                    </template>
                  </v-list>
                </v-card-text>
                <v-card-actions
                  v-if="isLessonsDownloadable(schedule)"
                  class="text-right"
                >
                  <v-container fluid>
                    <v-btn
                      outlined
                      color="success"
                      :href="
                        $route(
                          'classroom-schedules.download-lessons',
                          schedule.id
                        )
                      "
                      target="_blank"
                    >
                      <v-icon left>mdi-cloud-download</v-icon>
                      Download All
                    </v-btn>
                  </v-container>
                </v-card-actions>
              </v-card>
            </v-col>
            <v-col cols="12">
              <v-card class="mb-4">
                <v-card-title>Study Progress</v-card-title>
                <v-divider></v-divider>
                <v-card-text>
                  <v-list flat>
                    <template
                      v-for="(studyProgress, i) in studentStudyProgresses"
                    >
                      <v-list-item :key="studyProgress.student_id">
                        <v-list-item-content>
                          <v-container fluid class="pa-0">
                            <v-row align="center">
                              <v-col v-if="isTeacherAccount" cols="12" md="2">
                                <h3>{{ studyProgress.student_name }}</h3>
                              </v-col>
                              <v-col
                                cols="12"
                                :md="isTeacherAccount ? 7 : 12"
                                :xl="isTeacherAccount ? 8 : 12"
                              >
                                <v-row v-if="studyProgress.item != null" dense>
                                  <v-col
                                    v-for="(progressDetail, j) in studyProgress
                                      .item.details"
                                    :key="`pd-${j}`"
                                    cols="12"
                                    md="3"
                                    class="d-flex flex-wrap flex-column"
                                  >
                                    <h4 class="mb-2">
                                      {{ progressDetail.lesson_name }}
                                      <br />
                                      {{ progressDetail.lesson_name_cn }}
                                    </h4>
                                    <p>
                                      {{
                                        getCategoryName(
                                          progressDetail.lesson_category_id
                                        )
                                      }}
                                    </p>
                                    <v-spacer></v-spacer>
                                    <p>
                                      <span
                                        v-if="progressDetail.score_grade"
                                        class="font-weight-bold"
                                      >
                                        <v-chip
                                          small
                                          label
                                          :color="
                                            scoreGradeProps(
                                              progressDetail.score_num,
                                              'color'
                                            )
                                          "
                                        >
                                          Score:
                                          {{ progressDetail.score_grade }}
                                        </v-chip>
                                      </span>
                                      <span v-else class="text--disabled">
                                        No score yet
                                      </span>
                                    </p>
                                    <div
                                      v-if="progressDetail.notes"
                                      class="text-caption"
                                    >
                                      {{
                                        bilingualText(
                                          progressDetail,
                                          'notes',
                                          'notes_cn'
                                        )
                                      }}
                                    </div>
                                  </v-col>
                                </v-row>
                              </v-col>
                              <v-col
                                v-if="isTeacherAccount"
                                cols="12"
                                md="3"
                                xl="2"
                                class="text-right"
                              >
                                <v-btn
                                  v-if="studyProgress.item.id != null"
                                  depressed
                                  color="warning"
                                  @click="
                                    editStudyProgress(studyProgress.student_id)
                                  "
                                >
                                  <v-icon left>mdi-pencil</v-icon>
                                  Edit Progress
                                </v-btn>
                                <v-btn
                                  v-else
                                  depressed
                                  color="primary"
                                  @click="
                                    addStudyProgress(studyProgress.student_id)
                                  "
                                >
                                  <v-icon left>mdi-plus-circle</v-icon>
                                  Add Progress
                                </v-btn>
                              </v-col>
                            </v-row>
                          </v-container>
                        </v-list-item-content>
                      </v-list-item>
                      <v-divider
                        v-if="i < studentStudyProgresses.length - 1"
                        :key="i"
                      ></v-divider>
                    </template>
                  </v-list>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </v-container>
      </v-card-text>
    </v-card>
    <study-progress-dialog
      v-if="studyProgressDialog.show"
      v-model="studyProgressDialog.show"
      :item="studyProgressDialog.item"
      :lessons="schedule.lessons"
      :categories="categories"
      :is-last-session="isLastSession"
      submit-api
      @submit="onSubmitStudyProgress"
    ></study-progress-dialog>
  </v-container>
</template>
<script>
import constant from '@/constant'
import { mapActions, mapGetters } from 'vuex'
import StudyProgressDialog from '@components/ClassroomSchedule/StudyProgressDialog'
import RescheduleDialog from '@components/ClassroomSchedule/RescheduleDialog'
export default {
  name: 'ClassroomScheduleDetail',
  components: { StudyProgressDialog, RescheduleDialog },
  props: {
    schedule: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      classDateMenu: false,
      form: {
        classroom_id: null,
        session: null,
        month: null,
        term: null,
        class_date: null,
        start_time: '',
        end_time: '',
        study_progresses: [],
      },
      studyProgressDialog: {
        item: {
          id: null,
          student_id: null,
          classroom_schedule_id: this.schedule.id,
          notes: null,
          notes_cn: null,
          details: [],
        },
        show: false,
      },
      show: false,
    }
  },
  computed: {
    ...mapGetters({
      authUser: 'authUser',
      lessonList: 'lesson/list',
      isLessonListLoading: 'lesson/isListLoading',
      isLoading: 'classroomSchedule/isDetailLoading',
      scheduleDetail: 'classroomSchedule/detail',
      categoryList: 'lessonCategory/list',
    }),
    lessons() {
      return this.lessonList.data
    },
    scoreGradeOptions() {
      return constant.scoreGradeOptions
    },
    isRescheduleDisabled() {
      return moment(this.schedule.class_date).isBefore(moment(), 'date')
    },
    categories() {
      return this.categoryList.data
    },
    students() {
      return this.isStudentAccount
        ? this.schedule.classroom.students.filter(
            (e) => e.id === this.authUser.account_id
          )
        : this.schedule.classroom.students
    },
    studentStudyProgresses() {
      const studyProgresses = this.form.study_progresses
      return this.students.map((student) => {
        const studyProgress = studyProgresses.find(
          (e) => e.student_id === student.id
        )
        return {
          student_id: student.id,
          student_name: student.name,
          item: studyProgress ?? {
            id: null,
            student_id: student.id,
            classroom_schedule_id: this.schedule.id,
            notes: null,
            notes_cn: null,
            details: this.schedule.lessons.map((e) => ({
              lesson_id: e.id,
              lesson_name: e.name,
              lesson_name_cn: e.name_cn,
              lesson_category_id: e.lesson_category_id,
              score_num: null,
              score_grade: null,
              notes: null,
              notes_cn: null,
            })),
          },
        }
      })
    },
    isLastSession() {
      return (
        this.schedule.session ===
        this.schedule.classroom.subscription_package.class_per_week * 4
      )
    },
  },
  created() {
    this.init()
  },
  methods: {
    ...mapActions({
      getLessonList: 'lesson/getList',
      updateSchedule: 'classroomSchedule/update',
      getCategoryList: 'lessonCategory/getList',
    }),
    async init() {
      _.merge(this.form, this.schedule)
      await Promise.all([
        this.getLessonList({
          lesson_plan_id: this.form.classroom.lesson_plan_id,
        }),
        this.getCategoryList(),
      ])
    },
    getCategoryName(categoryId) {
      const category = this.categories.find((e) => e.id === categoryId)
      return category ? this.bilingualText(category, 'name', 'name_cn') : ''
    },
    addStudyProgress(studentId) {
      this.studyProgressDialog.item.student_id = studentId
      this.studyProgressDialog.show = true
    },
    editStudyProgress(studentId) {
      const studyProgress = this.form.study_progresses.find(
        (e) => e.student_id === studentId
      )
      this.studyProgressDialog.item = _.cloneDeep(studyProgress)
      this.studyProgressDialog.show = true
    },
    resetStudyProgressDialog() {
      this.studyProgressDialog.item = {
        id: null,
        student_id: null,
        classroom_schedule_id: this.schedule.id,
        notes: null,
        notes_cn: null,
        details: [],
      }
      this.studyProgressDialog.show = false
    },
    onSubmitStudyProgress(value) {
      const index = this.form.study_progresses.findIndex(
        (e) => e.student_id === value.student_id
      )
      if (index === -1) {
        this.form.study_progresses.push(_.cloneDeep(value))
      } else {
        this.$set(this.form.study_progresses, index, _.cloneDeep(value))
      }
      this.resetStudyProgressDialog()
    },
    onReschedule(value) {
      this.form.class_date = value.class_date
      this.form.start_time = value.start_time
      this.form.end_time = value.end_time
    },
    isLessonsDownloadable(schedule) {
      return (
        schedule.lessons &&
        schedule.lessons.length > 0 &&
        schedule.lessons.some((e) => e.attachment_file_url)
      )
    },
  },
}
</script>
